import { defineStore } from "pinia"
import { computed, markRaw, ref, shallowRef } from "vue"
import type { PDFDocumentProxy } from "pdfjs-dist"
import type { ReaderPdfBookManifest, ReaderPdfBookOverview, ReaderPdfPageMap, ReaderPdfTocItem } from "@/interface/DTO_Interfaces"
import { loadPdfOutlineToc, loadPdfToc } from "@/ts/pdf-outline"
import { loadReaderPdfBookOverview } from "@/ts/reader-pdf-book-overview"
import { defaultReaderPdfBookManifest } from "@/ts/reader-pdf-book-manifest"

export const pStore = defineStore("reader-pdf-book", () => {
        const bookManifest = shallowRef<ReaderPdfBookManifest>(defaultReaderPdfBookManifest)
        const pdfDocument = shallowRef<PDFDocumentProxy | null>(null)
        const pageMap = shallowRef<ReaderPdfPageMap>({ pages: [] })
        const sourceTocItems = shallowRef<ReaderPdfTocItem[]>([])
        const overview = shallowRef<ReaderPdfBookOverview | null>(null)
        const overviewLoadError = ref("")
        const hasStandaloneFirstPage = ref(false)
        const isLoading = ref(false)
        const loadError = ref("")
        let loadToken = 0
        let pendingLoad: Promise<boolean> | null = null
        let pendingOverviewLoad: Promise<boolean> | null = null
        let metadataLoad: Promise<{ pageMap: ReaderPdfPageMap; tocItems: ReaderPdfTocItem[] }> | null = null

        const isFacingPagesBook = computed(() => bookManifest.value.pdf.layout === "facing")
        const isDocumentReady = computed(() => pdfDocument.value !== null)
        const sourcePageCount = computed(() => pageMap.value.pages.length > 0
                ? Math.max(...pageMap.value.pages.map((entry) => entry.sourcePageNumber))
                : 0)
        const printedPageNumbers = computed(() => pageMap.value.pages.flatMap((entry) =>
                entry.pageNumber !== undefined ? [entry.pageNumber] : [entry.left, entry.right]))
        const firstPrintedPageNumber = computed(() => pageMap.value.pages.length > 0
                ? Math.min(...printedPageNumbers.value.filter((pageNumber) => !hasStandaloneFirstPage.value || pageNumber !== pageMap.value.pages[0]?.left))
                : bookManifest.value.firstPageNumber)
        const lastPrintedPageNumber = computed(() => pageMap.value.pages.length > 0
                ? Math.max(...printedPageNumbers.value)
                : bookManifest.value.firstPageNumber)
        const readingPageCount = computed(() => pageMap.value.pages.length > 0 ? lastPrintedPageNumber.value : 0)
        const overviewPageCount = computed(() => readingPageCount.value > 0
                ? bookManifest.value.overviewPageCount ?? readingPageCount.value
                : 0)

        // JSON-данные общие для обзора и ридера; их загрузка не открывает PDF.
        const loadMetadata = () => {
                if (metadataLoad) return metadataLoad

                const manifest = bookManifest.value
                const token = loadToken
                metadataLoad = (async () => {
                        const { loadPdfPageMap } = await import("@/ts/pdf-page-map")
                        const [loadedPageMap, tocItems] = await Promise.all([
                                loadPdfPageMap(manifest.pdf.pageMapSrc),
                                manifest.pdf.tocSrc ? loadPdfToc(manifest.pdf.tocSrc) : Promise.resolve(manifest.fallbackToc ?? []),
                        ])
                        if (token === loadToken) {
                                pageMap.value = loadedPageMap
                                sourceTocItems.value = tocItems
                        }
                        return { pageMap: loadedPageMap, tocItems }
                })().catch((error) => {
                        if (token === loadToken) metadataLoad = null
                        throw error
                })
                return metadataLoad
        }

        const releaseDocument = (document: PDFDocumentProxy | null) => {
                if (document) void document.destroy().catch((error) => console.warn("Не удалось освободить PDF-документ", error))
        }

        const selectBook = (manifest: ReaderPdfBookManifest) => {
                if (bookManifest.value === manifest) return

                // Незавершённая загрузка прежней книги не должна заменить данные новой.
                loadToken += 1
                pendingLoad = null
                pendingOverviewLoad = null
                metadataLoad = null
                releaseDocument(pdfDocument.value)
                bookManifest.value = manifest
                pdfDocument.value = null
                pageMap.value = { pages: [] }
                sourceTocItems.value = []
                overview.value = null
                overviewLoadError.value = ""
                hasStandaloneFirstPage.value = false
                isLoading.value = false
                loadError.value = ""
        }

        const loadOverview = (): Promise<boolean> => {
                if (overview.value) return Promise.resolve(true)
                if (pendingOverviewLoad) return pendingOverviewLoad

                const manifest = bookManifest.value
                const token = loadToken
                overviewLoadError.value = ""
                pendingOverviewLoad = (async () => {
                        try {
                                const [loadedOverview] = await Promise.all([
                                        loadReaderPdfBookOverview(manifest.pdf.overviewSrc),
                                        loadMetadata(),
                                ])
                                if (token !== loadToken) return false
                                overview.value = loadedOverview
                                return true
                        } catch (error) {
                                if (token === loadToken) overviewLoadError.value = error instanceof Error ? error.message : "Не удалось загрузить сведения об учебнике."
                                return false
                        } finally {
                                if (token === loadToken) pendingOverviewLoad = null
                        }
                })()
                return pendingOverviewLoad
        }

        const load = (): Promise<boolean> => {
                if (pdfDocument.value) return Promise.resolve(true)
                if (pendingLoad) return pendingLoad

                const manifest = bookManifest.value
                const token = loadToken
                isLoading.value = true
                loadError.value = ""

                pendingLoad = (async () => {
                        let loadedDocument: PDFDocumentProxy | null = null

                        try {
                                // PDF.js и его worker нужны только после открытия ридера.
                                const { loadPdfDocument } = await import("@/ts/pdf-document-loader")
                                if (token !== loadToken) return false

                                // Дожидаемся обеих операций, чтобы освободить PDF и при ошибке карты страниц.
                                const [documentResult, metadataResult] = await Promise.allSettled([
                                        loadPdfDocument(manifest.pdf.src),
                                        loadMetadata(),
                                ])
                                if (documentResult.status === "fulfilled") loadedDocument = documentResult.value
                                if (token !== loadToken) return false
                                if (documentResult.status === "rejected") throw documentResult.reason
                                if (metadataResult.status === "rejected") throw metadataResult.reason

                                const document = documentResult.value
                                const loadedPageMap = metadataResult.value.pageMap
                                const firstPage = await document.getPage(1)
                                const firstPageViewport = firstPage.getViewport({ scale: 1 })
                                const pageCount = Math.max(...loadedPageMap.pages.map((entry) => entry.sourcePageNumber))

                                if (token !== loadToken) return false
                                if (pageCount > document.numPages) throw new Error("Карта страниц выходит за пределы PDF")

                                const loadedToc = manifest.pdf.tocSrc
                                        ? metadataResult.value.tocItems
                                        : await loadPdfOutlineToc(document)

                                if (token !== loadToken) return false

                                pageMap.value = loadedPageMap
                                sourceTocItems.value = loadedToc
                                hasStandaloneFirstPage.value = manifest.pdf.layout !== "facing" && firstPageViewport.width < firstPageViewport.height
                                // PDF.js содержит внутреннее состояние и не должен превращаться в глубокий Vue Proxy.
                                pdfDocument.value = markRaw(document)
                                loadedDocument = null
                                return true
                        } catch (error) {
                                if (token === loadToken) loadError.value = error instanceof Error ? error.message : "Не удалось загрузить PDF через технический слой."
                                return false
                        } finally {
                                releaseDocument(loadedDocument)
                                if (token === loadToken) {
                                        isLoading.value = false
                                        pendingLoad = null
                                }
                        }
                })()

                return pendingLoad
        }

        return {
                bookManifest, pdfDocument, pageMap, sourceTocItems, hasStandaloneFirstPage,
                isLoading, loadError, isFacingPagesBook, isDocumentReady, sourcePageCount,
                printedPageNumbers, firstPrintedPageNumber, lastPrintedPageNumber, overview, overviewLoadError, readingPageCount, overviewPageCount,
                selectBook, loadOverview, load,
        }
})
